import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardFormsAnswersResult } from '@core/interfaces';
import { toPaginationOptions } from '@core/utils/to-pagination-options';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { isEmpty } from 'lodash';
import { merge, Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { sentry } from 'sentry';
import { ReportsService } from '../../reports.service';
import { FilterService } from '../filter/filter.service';
import { FormsReportService } from '../services/forms-report.service';

import { Location } from '@angular/common';

@Component({
  templateUrl: './dashboard-forms-list.component.html',
  styleUrls: ['./dashboard-forms-list.component.scss']
})
export class DashboardFormsListComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) private paginator: MatPaginator;
  private readonly removeSubscriptions = new Subject<void>();

  public isLoading: boolean;
  public totalCount: number;
  public formReportsPage: DashboardFormsAnswersResult;

  constructor(
    private readonly fuseProgressBarService: FuseProgressBarService,
    private readonly formsReportService: FormsReportService,
    private readonly reportsService: ReportsService,
    private readonly filterService: FilterService,
    private readonly route: ActivatedRoute,
    private router: Router,
    private location: Location,
  ) {
    this.isLoading = true;
    this.formReportsPage = null;
    this.filterService.selectedForm = null;
  }

  public ngOnInit(): void {
    merge(
      this.filterService.valueChanges.pipe(map(() => ({ source: 'filter' }))),
      this.paginator.page
    ).pipe(
      takeUntil(this.removeSubscriptions),
      filter(() => this.filterService.filterForm.valid),
      debounceTime(200),
      switchMap(() => this.updateData()),
    ).subscribe();

    if (this.filterService.filterForm.invalid) {
      this.filterService.filterForm.setValue(this.filterService.defaultFilterValue);
    }

    const queryParams = this.route.snapshot.queryParams;
    if (!isEmpty(queryParams)) {
      return;
    }
    this.filterService.reload();
  }

  ngOnDestroy(): void {
    if (!this.filterService.isFilterOn) {
      return;
    }

    if (this.filterService.filterForm.invalid) {
      return;
    }

    localStorage.setItem(`forms-dashboard-filter`, JSON.stringify(this.filterService.filterForm.value));
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  navigateToDetailPage(form: any): void {
    const where = this.filterService.toQuery(this.filterService.value);
    const defaultValues = this.filterService.toQuery(this.filterService.defaultFilterValue);
    const queryParams = this.reportsService.keepOnlyChangedValuesOnQueryParams(where, defaultValues);

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['relatorios/formularios/detalhes'], {
        queryParams: {
          formId_eq: form.formId,
          ...queryParams
        }
      })
    );
    console.log('url', url);
    this.filterService.selectedForm = { name: form.name };
    window.open(this.location.prepareExternalUrl(url), '_blank');
  }

  async updateData(): Promise<void> {
    this.totalCount = 0;
    this.isLoading = true;
    this.fuseProgressBarService.show();

    try {
      const where = this.filterService.toQuery(this.filterService.value);
      this.formReportsPage = await this.formsReportService
        .accountFormsAnswers(where,
          toPaginationOptions({
            paginator: this.paginator || {
              pageSize: 12,
              pageIndex: 0
            }
          })
        );

      this.totalCount = this.formReportsPage.totalCount;
    } catch (err) {
      sentry.captureException(err);
    } finally {
      this.isLoading = false;
      this.fuseProgressBarService.hide();
    }
  }
}
